<template>
  <v-container id="dashboard-manager-v2" fluid tag="section" class="dm-wrapper" style="padding: 24px 96px;">

    <!-- Header com Título à Esquerda e Filtros à Direita -->
    <div class="d-flex align-center justify-space-between mb-6">
      <!-- Título e Subtítulo à Esquerda -->
      <div class="d-flex align-center flex-grow-1">
        <div class="dm-header-icon mr-4">
          <v-icon color="#22d3ee" size="28">mdi-monitor-dashboard</v-icon>
        </div>
        <div>
          <div class="dm-header-title">
            Central do Time
            <span class="dm-header-version">v2</span>
          </div>
          <div class="dm-header-subtitle">Inteligência de engenharia dos conectores para decisões mais assertivas — Web, Mobile.</div>
        </div>
      </div>

      <!-- Filtros e Botões à Direita -->
      <div class="d-flex align-center" style="gap: 12px;">
        <!-- Filtro de Dias -->
        <v-select
          v-model="quickDateFilter"
          :items="quickDateOptions"
          prepend-inner-icon="mdi-clock-outline"
          dense
          outlined
          rounded="lg"
          hide-details
          class="dm-input"
          style="min-width: 160px;"
          @change="applyQuickFilter"
        />

        <!-- Botão Catálogo de Métricas -->
        <v-btn
          outlined
          small
          color="#22d3ee"
          class="text-none"
          style="font-weight: 600; border-color: #22d3ee;"
          @click="metricsCatalogOpen = true"
        >
          <v-icon left small>mdi-book-open-outline</v-icon>
          Catálogo de Métricas
        </v-btn>

        <!-- Botão Atualizar -->
        <v-btn
          icon
          small
          color="#22d3ee"
        >
          <v-icon small>mdi-refresh</v-icon>
        </v-btn>
      </div>
    </div>

    <template v-if="!isIndividualMode">
      <!-- Scorecard de Saúde do Time -->
      <v-card flat class="dm-v2-card pa-5 mb-6">
        <div class="mb-4">
          <h3 class="text-h6 font-weight-bold dm-text-main mb-1">Scorecard de Saúde do Time</h3>
          <p class="text-caption dm-text-muted mb-3">Desempenho de cada time frente às metas — GitHub, Jira, Azure Pipelines, SonarQube e Copilot</p>

          <!-- Legenda de Status -->
          <div class="d-flex align-center" style="gap: 20px;">
            <div class="d-flex align-center" style="gap: 6px;">
              <div style="width: 8px; height: 8px; border-radius: 50%; background-color: #22c55e;"></div>
              <span class="text-caption">Excelente</span>
            </div>
            <div class="d-flex align-center" style="gap: 6px;">
              <div style="width: 8px; height: 8px; border-radius: 50%; background-color: #eab308;"></div>
              <span class="text-caption">Bom</span>
            </div>
            <div class="d-flex align-center" style="gap: 6px;">
              <div style="width: 8px; height: 8px; border-radius: 50%; background-color: #f59e0b;"></div>
              <span class="text-caption">Médio</span>
            </div>
            <div class="d-flex align-center" style="gap: 6px;">
              <div style="width: 8px; height: 8px; border-radius: 50%; background-color: #ef4444;"></div>
              <span class="text-caption">Crítico</span>
            </div>
          </div>
        </div>

        <!-- Tabela de Scorecard -->
        <div class="dm-scorecard-table">
          <table style="width: 100%; border-collapse: collapse;">
            <thead>
              <tr>
                <th class="dm-scorecard-cell dm-scorecard-header">Time</th>
                <th class="dm-scorecard-cell dm-scorecard-header">Lead Time</th>
                <th class="dm-scorecard-cell dm-scorecard-header">Deploy/dia</th>
                <th class="dm-scorecard-cell dm-scorecard-header">Change Failure</th>
                <th class="dm-scorecard-cell dm-scorecard-header">Cobertura</th>
                <th class="dm-scorecard-cell dm-scorecard-header">Espera Review</th>
                <th class="dm-scorecard-cell dm-scorecard-header">Adoção IA</th>
                <th class="dm-scorecard-cell dm-scorecard-header">Saúde Geral</th>
              </tr>
            </thead>
            <tbody>
              <tr v-if="teamScorecard.length === 0">
                <td colspan="8" class="dm-scorecard-cell text-center dm-text-muted py-4">
                  <span v-if="loadingScorecard">Carregando...</span>
                  <span v-else>Nenhum time encontrado</span>
                </td>
              </tr>
              <tr v-for="team in teamScorecard" :key="team.name">
                <td class="dm-scorecard-cell">
                  <span :style="`color: ${team.color || '#a78bfa'};`">●</span> {{ team.name }}
                </td>
                <td class="dm-scorecard-cell" :style="`color: ${scorecardStatusColor(team.leadTime && team.leadTime.status)};`">
                  {{ team.leadTime ? team.leadTime.value + (team.leadTime.unit || 'd') : '--' }}
                </td>
                <td class="dm-scorecard-cell" :style="`color: ${scorecardStatusColor(team.deployPerDay && team.deployPerDay.status)};`">
                  {{ team.deployPerDay ? team.deployPerDay.value : '--' }}
                </td>
                <td class="dm-scorecard-cell" :style="`color: ${scorecardStatusColor(team.changeFailureRate && team.changeFailureRate.status)};`">
                  {{ team.changeFailureRate ? team.changeFailureRate.value + '%' : '--' }}
                </td>
                <td class="dm-scorecard-cell" :style="`color: ${scorecardStatusColor(team.testCoverage && team.testCoverage.status)};`">
                  {{ team.testCoverage ? team.testCoverage.value + '%' : '--' }}
                </td>
                <td class="dm-scorecard-cell" :style="`color: ${scorecardStatusColor(team.reviewWaitTime && team.reviewWaitTime.status)};`">
                  {{ team.reviewWaitTime ? team.reviewWaitTime.value + (team.reviewWaitTime.unit || 'h') : '--' }}
                </td>
                <td class="dm-scorecard-cell" :style="`color: ${scorecardStatusColor(team.aiAdoption && team.aiAdoption.status)};`">
                  {{ team.aiAdoption ? team.aiAdoption.value + '%' : '--' }}
                </td>
                <td class="dm-scorecard-cell" :style="`color: ${scorecardStatusColor(team.healthScore && team.healthScore.status)};`">
                  {{ team.healthScore ? team.healthScore.value : '--' }}
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </v-card>

      <!-- DORA · ENTREGA -->
      <div class="mb-8">
        <h3 class="text-h6 font-weight-bold dm-text-main mb-4">DORA · ENTREGA</h3>
        <div class="dm-dora-row">
          <v-card
            v-for="metric in doraMetricsArray"
            :key="metric.key"
            flat class="dm-v2-card dm-dora-card pa-4"
          >
            <div class="d-flex align-center justify-space-between mb-3">
              <span class="text-caption dm-text-muted">{{ metric.label }}</span>
              <v-chip x-small :color="doraLevelColor(metric.level)" text-color="white" class="font-weight-bold">
                {{ doraLevelLabel(metric.level) }}
              </v-chip>
            </div>
            <div class="text-h4 font-weight-bold dm-text-main mb-2">
              {{ metric.value }}<span class="text-h6">{{ metric.unit }}</span>
            </div>
            <div class="d-flex align-center mb-3">
              <span :style="`color: ${trendColor(metric.trend, metric.lowerIsBetter)}; font-weight: 600; font-size: 12px;`">
                {{ metric.trend }}
              </span>
              <span class="text-caption dm-text-muted ml-2">{{ metric.source }}</span>
            </div>
            <div class="dm-dora-chart" :style="`border-bottom: 2px solid ${doraLevelColor(metric.level)};`"></div>
          </v-card>
        </div>
      </div>

      <v-row class="mb-6" align="stretch">
        <v-col cols="12" md="6" class="d-flex">
          <v-card flat class="dm-v2-card flex-grow-1 d-flex flex-column pa-5">
            <div class="d-flex align-center mb-2">
              <v-icon color="#22d3ee" class="mr-3">mdi-filter-variant</v-icon>
              <span class="text-h6 font-weight-bold dm-text-main">Gargalos & Fluxo</span>
            </div>
            <p class="text-caption dm-text-muted mb-4">Onde o trabalho está parado — GitHub e Jira</p>

            <!-- Tempo por estágio do PR -->
            <div class="mb-4">
              <div class="text-caption font-weight-bold dm-text-main mb-2">Tempo por estágio do PR</div>
              <div class="d-flex align-center mb-2" :style="prStageBarStyle"></div>
              <div class="d-flex align-center" style="gap: 16px; flex-wrap: wrap;">
                <div class="d-flex align-center" style="gap: 4px;">
                  <div style="width: 8px; height: 8px; border-radius: 50%; background-color: #22d3ee;"></div>
                  <span class="text-caption">Código {{ flowBottlenecks.prStageBreakdown.code }}%</span>
                </div>
                <div class="d-flex align-center" style="gap: 4px;">
                  <div style="width: 8px; height: 8px; border-radius: 50%; background-color: #a855f7;"></div>
                  <span class="text-caption">Review {{ flowBottlenecks.prStageBreakdown.review }}%</span>
                </div>
                <div class="d-flex align-center" style="gap: 4px;">
                  <div style="width: 8px; height: 8px; border-radius: 50%; background-color: #22c55e;"></div>
                  <span class="text-caption">Merge {{ flowBottlenecks.prStageBreakdown.merge }}%</span>
                </div>
                <div class="d-flex align-center" style="gap: 4px;">
                  <div style="width: 8px; height: 8px; border-radius: 50%; background-color: #f97316;"></div>
                  <span class="text-caption">Deploy {{ flowBottlenecks.prStageBreakdown.deploy }}%</span>
                </div>
              </div>
            </div>

            <!-- PRs parados -->
            <div class="mb-4">
              <div class="text-caption font-weight-bold dm-text-main mb-3">PRs parados</div>
              <div class="d-flex flex-column" style="gap: 12px;">
                <div v-if="flowBottlenecks.stuckPRs.length === 0" class="text-caption dm-text-muted">Nenhum PR parado no período</div>
                <div
                  v-for="pr in flowBottlenecks.stuckPRs"
                  :key="pr.id"
                  class="d-flex align-center justify-space-between p-3"
                  style="background-color: rgba(255,255,255,0.02); border-radius: 8px; border-left: 3px solid #ef4444;"
                >
                  <div>
                    <div class="text-caption font-weight-bold" style="color: #666;">{{ pr.id }}</div>
                    <div class="text-body-2 font-weight-medium">{{ pr.title }}</div>
                    <div class="text-caption dm-text-muted">{{ pr.squad }} · @{{ pr.author }} · {{ pr.stage }}</div>
                  </div>
                  <div class="text-caption font-weight-bold" style="color: #f97316;">{{ pr.daysStuck }}d</div>
                </div>
              </div>
            </div>

            <!-- WIP envelhecido -->
            <div>
              <div class="text-caption font-weight-bold dm-text-main mb-3">WIP envelhecido</div>
              <div class="d-flex flex-column" style="gap: 12px;">
                <div v-if="flowBottlenecks.agedWIP.length === 0" class="text-caption dm-text-muted">Sem WIP envelhecido</div>
                <div
                  v-for="item in flowBottlenecks.agedWIP"
                  :key="item.id"
                  class="d-flex align-center justify-space-between p-3"
                  style="background-color: rgba(255,255,255,0.02); border-radius: 8px; border-left: 3px solid #60a5fa;"
                >
                  <div>
                    <div class="text-caption font-weight-bold" style="color: #666;">{{ item.id }}</div>
                    <div class="text-body-2 font-weight-medium">{{ item.title }}</div>
                    <div class="text-caption dm-text-muted">{{ item.squad }}</div>
                  </div>
                  <div class="text-caption font-weight-bold">{{ item.daysInProgress }}d</div>
                </div>
              </div>
            </div>
          </v-card>
        </v-col>

        <v-col cols="12" md="6" class="d-flex">
          <v-card flat class="dm-v2-card flex-grow-1 d-flex flex-column pa-5">
            <div class="d-flex align-center mb-2">
              <v-icon color="#f59e0b" class="mr-3">mdi-shield-alert-outline</v-icon>
              <span class="text-h6 font-weight-bold dm-text-main">Qualidade & Risco</span>
            </div>
            <p class="text-caption dm-text-muted mb-4">SonarQube e Azure Pipelines</p>

            <!-- Cobertura de Testes -->
            <div class="mb-5">
              <div class="d-flex align-center justify-space-between mb-2">
                <span class="text-caption font-weight-bold dm-text-main">Cobertura de Testes</span>
                <span class="text-caption" :style="`color: ${trendColor(qualityMetrics.coverage.trend, false)};`">{{ qualityMetrics.coverage.trend }}</span>
              </div>
              <div class="d-flex align-center" style="gap: 8px;">
                <div :style="coverageBarStyle(qualityMetrics.coverage.value)"></div>
                <span class="text-h6 font-weight-bold">{{ qualityMetrics.coverage.value }}%</span>
              </div>
            </div>

            <!-- Vulnerabilidades por severidade -->
            <div class="mb-5">
              <div class="text-caption font-weight-bold dm-text-main mb-3">Vulnerabilidades por severidade</div>
              <v-row class="ma-n2">
                <v-col cols="6" sm="3" class="pa-2">
                  <div class="d-flex flex-column align-center" style="padding: 12px; background-color: rgba(255,255,255,0.02); border-radius: 8px;">
                    <div class="text-h5 font-weight-bold" style="color: #ef4444;">{{ qualityMetrics.vulnerabilities.critical }}</div>
                    <div class="text-caption dm-text-muted">Crítica</div>
                  </div>
                </v-col>
                <v-col cols="6" sm="3" class="pa-2">
                  <div class="d-flex flex-column align-center" style="padding: 12px; background-color: rgba(255,255,255,0.02); border-radius: 8px;">
                    <div class="text-h5 font-weight-bold" style="color: #f97316;">{{ qualityMetrics.vulnerabilities.high }}</div>
                    <div class="text-caption dm-text-muted">Alta</div>
                  </div>
                </v-col>
                <v-col cols="6" sm="3" class="pa-2">
                  <div class="d-flex flex-column align-center" style="padding: 12px; background-color: rgba(255,255,255,0.02); border-radius: 8px;">
                    <div class="text-h5 font-weight-bold" style="color: #eab308;">{{ qualityMetrics.vulnerabilities.medium }}</div>
                    <div class="text-caption dm-text-muted">Média</div>
                  </div>
                </v-col>
                <v-col cols="6" sm="3" class="pa-2">
                  <div class="d-flex flex-column align-center" style="padding: 12px; background-color: rgba(255,255,255,0.02); border-radius: 8px;">
                    <div class="text-h5 font-weight-bold">{{ qualityMetrics.vulnerabilities.low }}</div>
                    <div class="text-caption dm-text-muted">Baixa</div>
                  </div>
                </v-col>
              </v-row>
            </div>

            <!-- Métricas de Qualidade -->
            <v-row class="ma-n2">
              <v-col cols="6" class="pa-2">
                <div style="padding: 12px; background-color: rgba(255,255,255,0.02); border-radius: 8px;">
                  <div class="text-caption dm-text-muted mb-1">Dívida Técnica</div>
                  <div class="text-h6 font-weight-bold">
                    {{ qualityMetrics.technicalDebt.value }}{{ qualityMetrics.technicalDebt.unit }}
                    <span class="text-caption" :style="`color: ${trendColor(qualityMetrics.technicalDebt.trend, true)};`">{{ qualityMetrics.technicalDebt.trend }}</span>
                  </div>
                </div>
              </v-col>
              <v-col cols="6" class="pa-2">
                <div style="padding: 12px; background-color: rgba(255,255,255,0.02); border-radius: 8px;">
                  <div class="text-caption dm-text-muted mb-1">Code Smells</div>
                  <div class="text-h6 font-weight-bold">{{ qualityMetrics.codeSmells }}</div>
                </div>
              </v-col>
              <v-col cols="6" class="pa-2">
                <div style="padding: 12px; background-color: rgba(255,255,255,0.02); border-radius: 8px;">
                  <div class="text-caption dm-text-muted mb-1">Duplicação</div>
                  <div class="text-h6 font-weight-bold">{{ qualityMetrics.duplication }}%</div>
                </div>
              </v-col>
              <v-col cols="6" class="pa-2">
                <div style="padding: 12px; background-color: rgba(255,255,255,0.02); border-radius: 8px;">
                  <div class="text-caption dm-text-muted mb-1">Quality Gate</div>
                  <div class="text-h6 font-weight-bold" :style="`color: ${qualityGateColor(qualityMetrics.qualityGate)};`">
                    {{ qualityGateLabel(qualityMetrics.qualityGate) }}
                  </div>
                </div>
              </v-col>
            </v-row>
          </v-card>
        </v-col>
      </v-row>

      <v-row class="mb-6" align="stretch">
        <v-col cols="12" md="6" class="d-flex">
          <v-card flat class="dm-v2-card flex-grow-1 d-flex flex-column pa-5">
            <div class="d-flex align-center mb-2">
              <v-icon color="#22d3ee" class="mr-3">mdi-chart-pie</v-icon>
              <span class="text-h6 font-weight-bold dm-text-main">Alocação de Investimento</span>
            </div>
            <p class="text-caption dm-text-muted mb-4">Onde o time investe o tempo — tipos e labels de issues (Jira)</p>

            <!-- Gráfico de Alocação -->
            <div class="d-flex align-center mb-4" :style="investmentBarStyle"></div>

            <!-- Legenda -->
            <div class="d-flex flex-column" style="gap: 8px;">
              <div class="d-flex align-center" style="gap: 8px;">
                <div style="width: 8px; height: 8px; border-radius: 50%; background-color: #22d3ee;"></div>
                <span class="text-caption">Novas Features <span style="font-weight: 600;">{{ investmentAllocation.features }}%</span></span>
              </div>
              <div class="d-flex align-center" style="gap: 8px;">
                <div style="width: 8px; height: 8px; border-radius: 50%; background-color: #f97316;"></div>
                <span class="text-caption">KTLO / Manutenção <span style="font-weight: 600;">{{ investmentAllocation.ktlo }}%</span></span>
              </div>
              <div class="d-flex align-center" style="gap: 8px;">
                <div style="width: 8px; height: 8px; border-radius: 50%; background-color: #ef4444;"></div>
                <span class="text-caption">Bugs / Incidentes <span style="font-weight: 600;">{{ investmentAllocation.bugs }}%</span></span>
              </div>
              <div class="d-flex align-center" style="gap: 8px;">
                <div style="width: 8px; height: 8px; border-radius: 50%; background-color: #a855f7;"></div>
                <span class="text-caption">Dívida Técnica <span style="font-weight: 600;">{{ investmentAllocation.techDebt }}%</span></span>
              </div>
              <div class="d-flex align-center" style="gap: 8px;">
                <div style="width: 8px; height: 8px; border-radius: 50%; background-color: #22c55e;"></div>
                <span class="text-caption">Suporte <span style="font-weight: 600;">{{ investmentAllocation.support }}%</span></span>
              </div>
            </div>
          </v-card>
        </v-col>

        <v-col cols="12" md="6" class="d-flex">
          <v-card flat class="dm-v2-card ai-glow-card flex-grow-1 d-flex flex-column pa-5">
            <div class="d-flex align-center justify-space-between mb-2">
              <div class="d-flex align-center">
                <v-icon color="#22d3ee" class="mr-3">mdi-creation</v-icon>
                <span class="text-h6 font-weight-bold dm-text-main">Alavanca de IA</span>
              </div>
              <v-chip x-small outlined color="#22d3ee" class="font-weight-bold">Copilot / ChatGPT</v-chip>
            </div>
            <p class="text-caption dm-text-muted mb-4">Adoção e impacto do Copilot</p>

            <!-- Métricas 2x2 -->
            <v-row class="ma-n2 mb-4">
              <v-col cols="6" class="pa-2">
                <div style="padding: 12px; background-color: rgba(255,255,255,0.02); border-radius: 8px;">
                  <div class="text-caption dm-text-muted mb-1">Adoção Ativa</div>
                  <div class="text-h6 font-weight-bold">
                    {{ copilotMetrics.adoption.value }}%
                    <span class="text-caption" :style="`color: ${trendColor(copilotMetrics.adoption.trend, false)};`">{{ copilotMetrics.adoption.trend }}</span>
                  </div>
                </div>
              </v-col>
              <v-col cols="6" class="pa-2">
                <div style="padding: 12px; background-color: rgba(255,255,255,0.02); border-radius: 8px;">
                  <div class="text-caption dm-text-muted mb-1">Taxa de Aceitação</div>
                  <div class="text-h6 font-weight-bold">{{ copilotMetrics.acceptanceRate }}%</div>
                </div>
              </v-col>
              <v-col cols="6" class="pa-2">
                <div style="padding: 12px; background-color: rgba(255,255,255,0.02); border-radius: 8px;">
                  <div class="text-caption dm-text-muted mb-1">Ganho em Cycle Time</div>
                  <div class="text-h6 font-weight-bold" style="color: #22c55e;">{{ copilotMetrics.cycleTimeGain }}%</div>
                </div>
              </v-col>
              <v-col cols="6" class="pa-2">
                <div style="padding: 12px; background-color: rgba(255,255,255,0.02); border-radius: 8px;">
                  <div class="text-caption dm-text-muted mb-1">Horas Economizadas</div>
                  <div class="text-h6 font-weight-bold">{{ copilotMetrics.hoursSaved }}h</div>
                </div>
              </v-col>
            </v-row>

            <!-- Seats ativos -->
            <div style="padding: 12px; background-color: rgba(255,255,255,0.02); border-radius: 8px;">
              <div class="d-flex align-center justify-space-between">
                <span class="text-caption dm-text-muted">Seats ativos</span>
                <span class="text-h6 font-weight-bold">{{ copilotMetrics.seats.active }} / {{ copilotMetrics.seats.total }}</span>
              </div>
            </div>
          </v-card>
        </v-col>
      </v-row>

      <v-row class="mb-6" align="stretch">
        <v-col cols="12" md="6" class="d-flex">
          <v-card flat class="dm-v2-card flex-grow-1 d-flex flex-column pa-5">
            <div class="d-flex align-center mb-2">
              <v-icon color="#a855f7" class="mr-3">mdi-calendar-check-outline</v-icon>
              <span class="text-h6 font-weight-bold dm-text-main">Previsibilidade</span>
            </div>
            <p class="text-caption dm-text-muted mb-4">{{ sprintPredictability.sprintName || 'Sprint Atual' }} · Jira</p>

            <!-- 3 Métricas em linha -->
            <v-row class="ma-n2 mb-4">
              <v-col cols="4" class="pa-2">
                <div style="padding: 12px; background-color: rgba(255,255,255,0.02); border-radius: 8px; text-align: center;">
                  <div class="text-caption dm-text-muted mb-1">Confiança</div>
                  <div class="text-h6 font-weight-bold">{{ sprintPredictability.confidence }}%</div>
                </div>
              </v-col>
              <v-col cols="4" class="pa-2">
                <div style="padding: 12px; background-color: rgba(255,255,255,0.02); border-radius: 8px; text-align: center;">
                  <div class="text-caption dm-text-muted mb-1">Scope Creep</div>
                  <div class="text-h6 font-weight-bold" style="color: #f97316;">{{ sprintPredictability.scopeCreep }}%</div>
                </div>
              </v-col>
              <v-col cols="4" class="pa-2">
                <div style="padding: 12px; background-color: rgba(255,255,255,0.02); border-radius: 8px; text-align: center;">
                  <div class="text-caption dm-text-muted mb-1">Risco de Prazo</div>
                  <div class="text-h6 font-weight-bold" :style="`color: ${deadlineRiskColor(sprintPredictability.deadlineRisk)};`">
                    {{ deadlineRiskLabel(sprintPredictability.deadlineRisk) }}
                  </div>
                </div>
              </v-col>
            </v-row>

            <!-- Burndown Chart -->
            <div>
              <div class="text-caption font-weight-bold dm-text-main mb-3">Burndown da Sprint</div>
              <svg viewBox="0 0 600 200" style="width: 100%; height: 150px;">
                <!-- Grid -->
                <line x1="40" y1="160" x2="580" y2="160" stroke="rgba(255,255,255,0.1)" stroke-width="1" />
                <!-- Y-axis -->
                <line x1="40" y1="20" x2="40" y2="160" stroke="rgba(255,255,255,0.2)" stroke-width="1" />
                <!-- Y-labels -->
                <text x="30" y="165" font-size="10" text-anchor="end" fill="rgba(255,255,255,0.5)">0</text>
                <text x="30" y="120" font-size="10" text-anchor="end" fill="rgba(255,255,255,0.5)">15</text>
                <text x="30" y="75" font-size="10" text-anchor="end" fill="rgba(255,255,255,0.5)">30</text>
                <text x="30" y="30" font-size="10" text-anchor="end" fill="rgba(255,255,255,0.5)">60</text>
                <!-- X-labels -->
                <text x="75" y="175" font-size="10" text-anchor="middle" fill="rgba(255,255,255,0.5)">D1</text>
                <text x="145" y="175" font-size="10" text-anchor="middle" fill="rgba(255,255,255,0.5)">D2</text>
                <text x="215" y="175" font-size="10" text-anchor="middle" fill="rgba(255,255,255,0.5)">D3</text>
                <text x="285" y="175" font-size="10" text-anchor="middle" fill="rgba(255,255,255,0.5)">D4</text>
                <text x="355" y="175" font-size="10" text-anchor="middle" fill="rgba(255,255,255,0.5)">D5</text>
                <text x="425" y="175" font-size="10" text-anchor="middle" fill="rgba(255,255,255,0.5)">D6</text>
                <text x="495" y="175" font-size="10" text-anchor="middle" fill="rgba(255,255,255,0.5)">D7</text>
                <text x="565" y="175" font-size="10" text-anchor="middle" fill="rgba(255,255,255,0.5)">D8</text>
                <!-- Ideal line (dashed) -->
                <line x1="40" y1="20" x2="580" y2="160" stroke="rgba(255,255,255,0.3)" stroke-width="2" stroke-dasharray="5,5" />
                <!-- Restante line (cyan) -->
                <polyline :points="burndownActualPolyline" fill="none" stroke="#22d3ee" stroke-width="2" />
                <!-- Legend -->
                <circle cx="50" cy="10" r="3" fill="#22d3ee" />
                <text x="60" y="13" font-size="10" fill="rgba(255,255,255,0.6)">Restante</text>
                <line x1="180" y1="8" x2="195" y2="8" stroke="rgba(255,255,255,0.3)" stroke-width="2" stroke-dasharray="3,3" />
                <text x="200" y="13" font-size="10" fill="rgba(255,255,255,0.6)">Ideal</text>
              </svg>
            </div>
          </v-card>
        </v-col>

        <v-col cols="12" md="6" class="d-flex">
          <v-card flat class="dm-v2-card flex-grow-1 d-flex flex-column pa-5">
            <div class="d-flex align-center mb-2">
              <v-icon color="#60a5fa" class="mr-3">mdi-account-group-outline</v-icon>
              <span class="text-h6 font-weight-bold dm-text-main">Pessoas & Operação</span>
            </div>
            <p class="text-caption dm-text-muted mb-4">Resumo de gestão de pessoas e plantão (PagerDuty)</p>

            <!-- Grid 2x3 de métricas -->
            <v-row class="ma-n2 mb-4">
              <v-col cols="6" class="pa-2">
                <div style="padding: 12px; background-color: rgba(255,255,255,0.02); border-radius: 8px; text-align: center;">
                  <div class="text-caption dm-text-muted mb-1">PDIs Pendentes</div>
                  <div class="text-h5 font-weight-bold">{{ peopleOps.pendingPDIs }}</div>
                </div>
              </v-col>
              <v-col cols="6" class="pa-2">
                <div style="padding: 12px; background-color: rgba(255,255,255,0.02); border-radius: 8px; text-align: center;">
                  <div class="text-caption dm-text-muted mb-1">Reuniões 1:1</div>
                  <div class="text-h5 font-weight-bold">{{ peopleOps.upcomingOneOnOnes }}</div>
                </div>
              </v-col>
              <v-col cols="6" class="pa-2">
                <div style="padding: 12px; background-color: rgba(255,255,255,0.02); border-radius: 8px; text-align: center;">
                  <div class="text-caption dm-text-muted mb-1">Feedbacks</div>
                  <div class="text-h5 font-weight-bold">{{ peopleOps.feedbacks }}</div>
                </div>
              </v-col>
              <v-col cols="6" class="pa-2">
                <div style="padding: 12px; background-color: rgba(255,255,255,0.02); border-radius: 8px; text-align: center;">
                  <div class="text-caption dm-text-muted mb-1">Competências</div>
                  <div class="text-h5 font-weight-bold">{{ peopleOps.competencias }}</div>
                </div>
              </v-col>
            </v-row>

            <!-- De plantão agora -->
            <div>
              <div class="text-caption font-weight-bold dm-text-main mb-3">De plantão agora</div>
              <div class="d-flex flex-column" style="gap: 12px;">
                <div v-if="onCall.length === 0" class="text-caption dm-text-muted">Sem dados de plantão disponíveis</div>
                <div
                  v-for="person in onCall"
                  :key="person.name"
                  class="d-flex align-center justify-space-between p-3"
                  style="background-color: rgba(255,255,255,0.02); border-radius: 8px;"
                >
                  <div>
                    <div class="text-body-2 font-weight-medium">{{ person.name }}</div>
                    <div class="text-caption dm-text-muted">{{ person.squad }}</div>
                  </div>
                  <div class="d-flex align-center" style="gap: 8px;">
                    <span class="text-caption dm-text-muted">{{ person.hoursOnDuty }}h</span>
                    <v-chip x-small color="#22c55e" text-color="white" class="font-weight-bold">{{ person.status || 'OK' }}</v-chip>
                  </div>
                </div>
              </div>
            </div>
          </v-card>
        </v-col>
      </v-row>

      <v-row class="mb-8">
        <v-col cols="12">
          <v-card flat class="dm-v2-card d-flex flex-column pa-0 overflow-hidden">
            <div class="pa-5 border-bottom dm-border-color d-flex align-center justify-space-between">
              <div class="d-flex align-center">
                <v-icon color="#22d3ee" class="mr-3">mdi-lightning-bolt</v-icon>
                <span class="text-h6 font-weight-bold dm-text-main">Insights & Alertas</span>
              </div>
              <v-btn
                small
                color="#22d3ee"
                depressed
                class="text-none"
                style="border-radius: 8px; padding: 6px 16px; font-weight: 600;"
              >
                <v-icon left x-small>mdi-brain</v-icon>
                Gerar análise com IA
              </v-btn>
            </div>
            <v-card-text class="pa-5">
              <div v-if="loadingInsights" class="d-flex align-center justify-center" style="min-height: 200px;">
                <v-progress-circular indeterminate color="#22d3ee" size="40" />
              </div>
              <div v-else-if="insights.length > 0" class="d-flex flex-column" style="gap: 12px;">
                <div
                  v-for="insight in insights"
                  :key="insight.id"
                  class="d-flex align-start p-4"
                  style="background-color: rgba(255,255,255,0.02); border-radius: 8px; border-left: 3px solid; gap: 12px;"
                  :style="{ borderColor: getInsightColor(insight.type) }"
                >
                  <!-- Icon -->
                  <v-icon
                    size="20"
                    :color="getInsightColor(insight.type)"
                    style="flex-shrink: 0; margin-top: 2px;"
                  >
                    {{ getInsightIcon(insight.type) }}
                  </v-icon>

                  <!-- Content -->
                  <div style="flex: 1; min-width: 0;">
                    <div class="d-flex align-center mb-1">
                      <span class="text-body-2 font-weight-bold dm-text-main">{{ insight.title }}</span>
                      <v-chip
                        v-if="insight.type"
                        x-small
                        outlined
                        class="ml-2"
                        style="border-color: rgba(255,255,255,0.2); color: rgba(255,255,255,0.6); font-size: 11px;"
                      >
                        {{ getSourceLabel(insight.type) }}
                      </v-chip>
                    </div>
                    <p class="text-caption dm-text-muted mb-0" style="word-break: break-word;">
                      {{ insight.summary }}
                    </p>
                  </div>

                  <!-- Action Button -->
                  <v-btn
                    text
                    x-small
                    color="#22d3ee"
                    style="flex-shrink: 0; white-space: nowrap; font-weight: 600;"
                  >
                    {{ getActionLabel(insight.type) }}
                    <v-icon right x-small>mdi-arrow-right</v-icon>
                  </v-btn>
                </div>
              </div>
              <div v-else class="d-flex align-center justify-center" style="min-height: 120px;">
                <span class="dm-text-muted text-caption">Nenhum insight disponível no momento</span>
              </div>
            </v-card-text>
          </v-card>
        </v-col>
      </v-row>

      <!-- ========================================== -->
      <!-- ÁREA DOS GRÁFICOS DO CATÁLOGO DE MÉTRICAS -->
      <!-- ========================================== -->
      <div class="d-flex align-center justify-space-between mb-6 mt-4">
        <div>
          <h3 class="text-h5 font-weight-bold dm-text-main">Métricas Fixadas</h3>
          <p class="text-caption dm-text-muted mb-0">Customize este painel com as métricas mais relevantes do catálogo.</p>
        </div>
        <v-btn depressed color="#22d3ee" class="black--text font-weight-bold px-5" @click="metricsCatalogOpen = true">
          <v-icon left size="20">mdi-plus-box-multiple-outline</v-icon> Adicionar Gráficos
        </v-btn>
      </div>

      <div v-if="totalFavoriteMetrics === 0" class="text-center py-12 rounded-xl mb-6" style="border: 1px dashed rgba(255,255,255,0.1); background: rgba(255,255,255,0.01);">
        <v-icon size="64" color="rgba(255,255,255,0.1)">mdi-view-dashboard-variant-outline</v-icon>
        <div class="text-h6 dm-text-main font-weight-bold mt-4">Painel Limpo</div>
        <div class="text-body-1 dm-text-muted mt-1 mb-6">Você ainda não possui métricas fixadas para exibição rápida.</div>
        <v-btn outlined color="#22d3ee" @click="metricsCatalogOpen = true">
          Explorar Catálogo DORA
        </v-btn>
      </div>

      <div v-for="group in favoriteMetricsGrouped" :key="group.source" class="mb-8">
        <div class="d-flex align-center mb-4 pl-1">
          <v-icon :color="getSourceColor(group.source)" class="mr-2">{{ getSourceIcon(group.source) }}</v-icon>
          <span class="text-subtitle-1 font-weight-bold dm-text-main mr-3">{{ group.source }}</span>
          <v-chip small outlined color="#22d3ee">{{ group.metrics.length }} ativas</v-chip>
        </div>

        <v-row>
          <v-col v-for="metric in group.metrics" :key="metric.id" cols="12" md="6">
            <v-card flat class="dm-v2-card h-100 d-flex flex-column">
              <v-card-title class="pa-5 pb-2 d-flex justify-space-between align-start">
                <div>
                  <div class="text-subtitle-1 font-weight-bold dm-text-main" style="line-height: 1.2">{{ metric.name }}</div>
                  <div class="text-caption dm-text-muted mt-1">{{ metric.description }}</div>
                </div>
                <v-btn icon small @click="toggleFavoriteMetric(metric.id)">
                  <v-icon color="#22d3ee">mdi-star</v-icon>
                </v-btn>
              </v-card-title>
              <v-card-text class="flex-grow-1 px-5 pb-5 pt-2">
                <div style="position: relative; height: 260px; width: 100%;">
                  <canvas :ref="'favoriteChart_' + metric.id"></canvas>
                </div>
              </v-card-text>
            </v-card>
          </v-col>
        </v-row>
      </div>

    </template>
    
    <template v-else>
      <v-btn text color="#22d3ee" class="mb-4 pl-0" @click="selectedUserFilter = 'all'">
        <v-icon left>mdi-arrow-left</v-icon> Voltar para Central do Time
      </v-btn>
      <v-card flat class="dm-v2-card pa-10 text-center">
        <v-icon size="64" color="#22d3ee" class="mb-4">mdi-account-hard-hat</v-icon>
        <h2 class="text-h5 dm-text-main font-weight-bold">Visão Individual (Mock)</h2>
        <p class="dm-text-muted mt-2">Área reservada para detalhamento do colaborador selecionado.</p>
      </v-card>
    </template>

    <!-- DRAWER: CATÁLOGO DE MÉTRICAS -->
    <v-navigation-drawer
      v-model="metricsCatalogOpen"
      temporary
      right
      width="450"
      fixed
      class="dm-drawer"
    >
      <div class="d-flex flex-column h-100">
        <div class="pa-6 border-bottom dm-border-color shrink-0">
          <div class="d-flex justify-space-between align-start mb-2">
            <div>
              <h3 class="text-h5 font-weight-bold dm-text-main">Métricas</h3>
              <p class="text-body-2 dm-text-muted mb-0">DORA, Agilidade e Qualidade</p>
            </div>
            <v-btn icon @click="metricsCatalogOpen = false">
              <v-icon>mdi-close</v-icon>
            </v-btn>
          </div>
        </div>

        <div class="pa-6 pb-2 shrink-0">
          <v-text-field
            v-model="metricsCatalogSearch"
            placeholder="Buscar..."
            outlined
            dense
            hide-details
            prepend-inner-icon="mdi-magnify"
            clearable
            class="mb-4"
          />

          <div class="mb-3">
            <div class="text-caption font-weight-bold text-uppercase dm-text-muted mb-2">Fonte de Dados</div>
            <div class="d-flex flex-wrap" style="gap: 8px;">
              <v-chip
                v-for="src in catalogSourceOptions"
                :key="src"
                small
                :outlined="metricsCatalogSourceFilter !== src"
                :color="metricsCatalogSourceFilter === src ? '#22d3ee' : ''"
                class="font-weight-bold"
                @click="metricsCatalogSourceFilter = src"
              >{{ src }}</v-chip>
            </div>
          </div>

          <div>
            <div class="text-caption font-weight-bold text-uppercase dm-text-muted mb-2">Categoria</div>
            <div class="d-flex flex-wrap" style="gap: 8px;">
              <v-chip
                v-for="cat in catalogCategoryOptions"
                :key="cat"
                small
                :outlined="metricsCatalogCategoryFilter !== cat"
                :color="metricsCatalogCategoryFilter === cat ? '#22d3ee' : ''"
                class="font-weight-bold"
                @click="metricsCatalogCategoryFilter = cat"
              >{{ cat }}</v-chip>
            </div>
          </div>
        </div>

        <div class="flex-grow-1 overflow-y-auto pa-4">
          <div v-if="filteredCatalogGrouped.length === 0" class="text-center py-10">
            <v-icon size="48" color="rgba(255,255,255,0.1)">mdi-database-search</v-icon>
            <p class="text-body-2 dm-text-muted mt-4">Nenhuma métrica encontrada para os filtros aplicados.</p>
          </div>

          <div v-for="group in filteredCatalogGrouped" :key="group.source" class="mb-6">
            <div class="d-flex align-center mb-3 pl-2">
              <v-icon small :color="getSourceColor(group.source)" class="mr-2">{{ getSourceIcon(group.source) }}</v-icon>
              <span class="text-body-2 font-weight-bold dm-text-main text-uppercase">{{ group.source }}</span>
            </div>

            <v-list class="transparent pa-0">
              <v-list-item
                v-for="metric in group.metrics"
                :key="metric.id"
                class="mb-2 rounded-lg pa-3"
                style="background: rgba(255,255,255,0.02); border: 1px solid rgba(255,255,255,0.07);"
              >
                <v-list-item-content class="pa-0">
                  <div class="d-flex justify-space-between align-start">
                    <div class="pr-3">
                      <div class="text-body-2 font-weight-bold dm-text-main mb-1">{{ metric.name }}</div>
                      <div class="text-caption dm-text-muted mb-2" style="line-height: 1.4;">{{ metric.description }}</div>
                      <v-chip
                        x-small
                        outlined
                        :style="`color: ${getCategoryColor(metric.category)}; border-color: ${getCategoryColor(metric.category)}40;`"
                      >{{ metric.category }}</v-chip>
                    </div>
                    <v-btn
                      icon
                      @click="toggleFavoriteMetric(metric.id)"
                    >
                      <v-icon :color="isMetricFavorite(metric.id) ? '#22d3ee' : 'rgba(255,255,255,0.2)'">
                        {{ isMetricFavorite(metric.id) ? 'mdi-star' : 'mdi-star-outline' }}
                      </v-icon>
                    </v-btn>
                  </div>
                </v-list-item-content>
              </v-list-item>
            </v-list>
          </div>
        </div>
      </div>
    </v-navigation-drawer>

  </v-container>
</template>

<script>
import globalViewMode from '@/mixins/globalViewMode'
import api from '@/services/api'
import { ManagerService } from '@/services'
import Chart from 'chart.js'

export default {
  name: 'DashboardManagerV2',
  mixins: [globalViewMode],

  data: () => ({
    // Filtros
    selectedTeamFilter: 'all',
    selectedUserFilter: 'all',
    quickDateFilter: 30,
    quickDateOptions: [
      { text: 'Últimos 7 dias', value: 7 },
      { text: 'Últimos 15 dias', value: 15 },
      { text: 'Últimos 30 dias', value: 30 },
      { text: 'Últimos 60 dias', value: 60 }
    ],

    teamFilterOptions: [
      { id: 'all', name: 'Todos os Times' },
      { id: 't1', name: 'Frontend Squad' },
      { id: 't2', name: 'Backend Services' }
    ],
    userFilterOptions: [{ id: 'all', name: 'Todos os Colaboradores' }],
    loadingTeamUsers: false,

    // Loading geral
    loading: false,

    // DORA Metrics — /api/manager/v2/dora-metrics
    loadingDora: false,
    doraMetrics: {
      deployFrequency: { value: '--', unit: '/dia', level: 'ELITE',  trend: '', source: 'Azure Pipelines', lowerIsBetter: false },
      leadTime:        { value: '--', unit: 'd',    level: 'ALTO',   trend: '', source: 'Jira',            lowerIsBetter: true  },
      changeFailure:   { value: '--', unit: '%',    level: 'ALTO',   trend: '', source: 'Azure Pipelines', lowerIsBetter: true  },
      mttr:            { value: '--', unit: 'h',    level: 'ELITE',  trend: '', source: 'PagerDuty',       lowerIsBetter: true  },
      reworkRate:      { value: '--', unit: '%',    level: 'MEDIO',  trend: '', source: 'GitHub',          lowerIsBetter: true  },
    },

    // Team Scorecard — /api/manager/v2/team-scorecard
    loadingScorecard: false,
    teamScorecard: [],

    // Flow Bottlenecks — /api/manager/v2/flow-bottlenecks
    loadingFlow: false,
    flowBottlenecks: {
      prStageBreakdown: { code: 32, review: 41, merge: 16, deploy: 11 },
      stuckPRs: [],
      agedWIP: [],
    },

    // Quality Metrics — /api/manager/v2/quality-metrics
    loadingQuality: false,
    qualityMetrics: {
      coverage: { value: 0, trend: '' },
      vulnerabilities: { critical: 0, high: 0, medium: 0, low: 0 },
      technicalDebt: { value: 0, unit: 'd', trend: '' },
      codeSmells: 0,
      duplication: 0,
      qualityGate: 'UNKNOWN',
    },

    // Investment Allocation — /api/manager/v2/investment-allocation
    loadingAllocation: false,
    investmentAllocation: { features: 48, ktlo: 18, bugs: 14, techDebt: 13, support: 7 },

    // Copilot Metrics — /api/manager/v2/copilot-metrics
    loadingCopilot: false,
    copilotMetrics: {
      adoption: { value: '--', trend: '' },
      acceptanceRate: '--',
      cycleTimeGain: '--',
      hoursSaved: '--',
      seats: { active: '--', total: '--' },
    },

    // Sprint Predictability — /api/manager/v2/sprint-predictability
    loadingPredictability: false,
    sprintPredictability: {
      sprintName: '',
      confidence: 0,
      scopeCreep: 0,
      deadlineRisk: 'MEDIUM',
      burndown: { labels: [], ideal: [], actual: [] },
    },

    // People & Ops — endpoints V1
    loadingPeopleOps: false,
    peopleOps: { pendingPDIs: 0, upcomingOneOnOnes: 0, feedbacks: 0, competencias: 0 },

    // On-call — /api/manager/v2/on-call
    loadingOnCall: false,
    onCall: [],

    // Insights & Alertas
    insights: [],
    loadingInsights: false,

    // ==========================================
    // ESTADO DO CATÁLOGO DE MÉTRICAS
    // ==========================================
    metricsCatalogOpen: false,
    metricsCatalogSearch: '',
    metricsCatalogSourceFilter: 'Todos',
    metricsCatalogCategoryFilter: 'Todas',
    catalogSourceOptions: ['Todos', 'GitHub', 'Jira', 'Azure Pipelines', 'SonarQube'],
    catalogCategoryOptions: ['Todas', 'Code Review', 'Entrega', 'Qualidade', 'CI/CD', 'Produtividade'],
    favoriteMetricIds: [],
    favoriteMetricsData: {},
    favoriteChartInstances: {},
    loadingFavorites: false,
    dynamicColorPalette: [
      '#FF5722', '#2196F3', '#FF9800', '#4CAF50', '#9C27B0',
      '#00BCD4', '#E91E63', '#607D8B', '#795548', '#3F51B5',
    ],
    mockVelocityData: {
      teamAvg: 87,
      tasksPerMonth: 24,
      prsPerMonth: 10,
      members: [
        { name: 'Ana Silva', score: 95, trend: 1, tasks: 28, prs: 12 },
        { name: 'Carlos Lima', score: 88, trend: 1, tasks: 24, prs: 10 },
        { name: 'Maria Santos', score: 82, trend: 0, tasks: 22, prs: 8 },
        { name: 'Pedro Costa', score: 76, trend: -1, tasks: 18, prs: 6 },
      ],
    },
    allAvailableMetrics: [
      // GitHub
      { id: 'github-pr-cycle-time', name: 'PR Cycle Time por Repositório', description: 'Tempo médio em horas do PR aberto até merge', source: 'GitHub', category: 'Code Review', chartType: 'line', legend: [{ label: 'Frontend', color: '#FF5722' }, { label: 'Backend', color: '#2196F3' }, { label: 'Mobile', color: '#FF9800' }, { label: 'Média Geral', color: '#4CAF50', dashed: true }] },
      { id: 'github-prs-semana', name: 'PRs Criados vs Mergeados', description: 'Volume semanal de Pull Requests', source: 'GitHub', category: 'Entrega', chartType: 'bar', legend: [{ label: 'PRs Criados', color: '#FF5722' }, { label: 'PRs Mergeados', color: '#4CAF50' }] },
      { id: 'github-pr-time-breakdown', name: 'PR Time Breakdown', description: 'Distribuição de tempo entre etapas do PR', source: 'GitHub', category: 'Code Review', chartType: 'bar', legend: [{ label: 'Abertura → Review', color: '#FF9800' }, { label: 'Review → Merge', color: '#2196F3' }] },
      { id: 'github-top-reviewers', name: 'Top Reviewers', description: 'Ranking dos revisores mais ativos', source: 'GitHub', category: 'Code Review', chartType: 'bar', legend: [{ label: 'Reviews realizados', color: '#9C27B0' }] },
      { id: 'github-distribuicao-comentarios', name: 'Distribuição de Comentários', description: 'Comentários por PR e por revisor', source: 'GitHub', category: 'Code Review', chartType: 'bar', legend: [{ label: 'Comentários', color: '#00BCD4' }] },
      { id: 'github-taxa-aprovacao', name: 'Taxa de Aprovação de PRs', description: 'Percentual de PRs aprovados sem mudanças solicitadas', source: 'GitHub', category: 'Qualidade', chartType: 'line', legend: [{ label: 'Taxa de Aprovação (%)', color: '#4CAF50' }] },
      { id: 'github-commits-autor', name: 'Commits por Autor', description: 'Volume de commits por desenvolvedor nos últimos 30 dias', source: 'GitHub', category: 'Produtividade', chartType: 'bar', legend: [{ label: 'Commits', color: '#22d3ee' }] },
      { id: 'github-issues-evolucao', name: 'Issues Abertas vs Fechadas', description: 'Evolução de issues abertas e resolvidas por semana', source: 'GitHub', category: 'Entrega', chartType: 'bar', legend: [{ label: 'Abertas', color: '#F44336' }, { label: 'Fechadas', color: '#4CAF50' }] },
      { id: 'github-hotfix-rate', name: 'Hotfix Rate', description: 'Proporção de hotfixes em relação a releases planejadas', source: 'GitHub', category: 'Qualidade', chartType: 'line', legend: [{ label: 'Hotfixes (%)', color: '#F44336' }] },
      { id: 'github-branch-lifetime', name: 'Branch Lifetime', description: 'Tempo médio de vida das branches antes do merge', source: 'GitHub', category: 'Code Review', chartType: 'line', legend: [{ label: 'Dias médios', color: '#607D8B' }] },
      // Jira
      { id: 'jira-task-cycle-time', name: 'Task Cycle Time por Projeto', description: 'Tempo médio em dias do início até conclusão', source: 'Jira', category: 'Entrega', chartType: 'area', dynamicLegend: true },
      { id: 'jira-velocidade-individual', name: 'Velocidade Individual e do Time', description: 'Métricas de produtividade dos últimos 30 dias', source: 'Jira', category: 'Produtividade', chartType: 'velocity-table' },
      { id: 'jira-lead-time', name: 'Lead Time', description: 'Tempo desde criação até conclusão da task', source: 'Jira', category: 'Entrega', chartType: 'line', legend: [{ label: 'Lead Time (dias)', color: '#9C27B0' }] },
      { id: 'jira-burndown', name: 'Burndown por Sprint', description: 'Progresso de conclusão ao longo do sprint', source: 'Jira', category: 'Entrega', chartType: 'line', legend: [{ label: 'Ideal', color: '#2196F3', dashed: true }, { label: 'Real', color: '#22d3ee' }] },
      { id: 'jira-bugs-rate', name: 'Taxa de Bugs por Projeto', description: 'Percentual de tasks que são bugs por projeto', source: 'Jira', category: 'Qualidade', chartType: 'bar', legend: [{ label: 'Bugs (%)', color: '#F44336' }] },
      { id: 'jira-throughput', name: 'Throughput do Time', description: 'Número de tasks concluídas por semana', source: 'Jira', category: 'Produtividade', chartType: 'bar', legend: [{ label: 'Tasks concluídas', color: '#4CAF50' }] },
      // Azure Pipelines
      { id: 'azure-build-success', name: 'Taxa de Build Success', description: 'Percentual de builds bem-sucedidos por pipeline', source: 'Azure Pipelines', category: 'CI/CD', chartType: 'line', legend: [{ label: 'Sucesso (%)', color: '#4CAF50' }] },
      { id: 'azure-deploy-frequency', name: 'Frequência de Deploy', description: 'Número de deploys realizados por semana', source: 'Azure Pipelines', category: 'CI/CD', chartType: 'bar', legend: [{ label: 'Deploys', color: '#2196F3' }] },
      { id: 'azure-pipeline-duration', name: 'Duração Média de Pipeline', description: 'Tempo médio de execução do pipeline em minutos', source: 'Azure Pipelines', category: 'CI/CD', chartType: 'line', legend: [{ label: 'Duração (min)', color: '#FF9800' }] },
      // SonarQube
      { id: 'sonar-code-coverage', name: 'Cobertura de Código', description: 'Percentual de linhas de código cobertas por testes', source: 'SonarQube', category: 'Qualidade', chartType: 'line', legend: [{ label: 'Cobertura (%)', color: '#4CAF50' }] },
      { id: 'sonar-code-smells', name: 'Code Smells', description: 'Número de problemas de qualidade identificados', source: 'SonarQube', category: 'Qualidade', chartType: 'bar', legend: [{ label: 'Code Smells', color: '#FF9800' }] },
      { id: 'sonar-technical-debt', name: 'Dívida Técnica', description: 'Estimativa de tempo para corrigir problemas de código', source: 'SonarQube', category: 'Qualidade', chartType: 'line', legend: [{ label: 'Horas estimadas', color: '#9C27B0' }] },
    ],
  }),

  computed: {
    isIndividualMode() {
      return this.selectedUserFilter !== 'all'
    },

    totalFavoriteMetrics() {
      return this.favoriteMetricIds.length
    },

    favoriteMetrics() {
      return this.allAvailableMetrics.filter(m => this.favoriteMetricIds.includes(m.id))
    },

    favoriteMetricsGrouped() {
      const groups = {}
      this.favoriteMetrics.forEach(metric => {
        if (!groups[metric.source]) {
          groups[metric.source] = { source: metric.source, metrics: [] }
        }
        groups[metric.source].metrics.push(metric)
      })
      return Object.values(groups)
    },

    filteredCatalogMetrics() {
      const search = (this.metricsCatalogSearch || '').toLowerCase()
      return this.allAvailableMetrics.filter(m => {
        const matchSource = this.metricsCatalogSourceFilter === 'Todos' || m.source === this.metricsCatalogSourceFilter
        const matchCategory = this.metricsCatalogCategoryFilter === 'Todas' || m.category === this.metricsCatalogCategoryFilter
        const matchSearch = !search || m.name.toLowerCase().includes(search) || m.description.toLowerCase().includes(search)
        return matchSource && matchCategory && matchSearch
      })
    },

    filteredCatalogGrouped() {
      const groups = {}
      this.filteredCatalogMetrics.forEach(metric => {
        if (!groups[metric.source]) {
          groups[metric.source] = { source: metric.source, metrics: [] }
        }
        groups[metric.source].metrics.push(metric)
      })
      return Object.values(groups)
    },

    doraMetricsArray() {
      const m = this.doraMetrics
      return [
        { key: 'deployFrequency', label: 'Frequência de Deploy', lowerIsBetter: false, ...m.deployFrequency },
        { key: 'leadTime',        label: 'Lead Time',            lowerIsBetter: true,  ...m.leadTime        },
        { key: 'changeFailure',   label: 'Change Failure Rate',  lowerIsBetter: true,  ...m.changeFailure   },
        { key: 'mttr',            label: 'MTTR',                 lowerIsBetter: true,  ...m.mttr            },
        { key: 'reworkRate',      label: 'Rework Rate',          lowerIsBetter: true,  ...m.reworkRate      },
      ]
    },

    prStageBarStyle() {
      const s = this.flowBottlenecks.prStageBreakdown
      const c = s.code || 0
      const r = c + (s.review || 0)
      const m = r + (s.merge || 0)
      return `height: 12px; background: linear-gradient(to right, #22d3ee ${c}%, #a855f7 ${c}%, #a855f7 ${r}%, #22c55e ${r}%, #22c55e ${m}%, #f97316 ${m}%); border-radius: 6px;`
    },

    investmentBarStyle() {
      const a = this.investmentAllocation
      const f = a.features || 0
      const k = f + (a.ktlo || 0)
      const b = k + (a.bugs || 0)
      const t = b + (a.techDebt || 0)
      return `height: 16px; background: linear-gradient(to right, #22d3ee ${f}%, #f97316 ${f}%, #f97316 ${k}%, #ef4444 ${k}%, #ef4444 ${b}%, #a855f7 ${b}%, #a855f7 ${t}%, #22c55e ${t}%); border-radius: 8px;`
    },

    burndownActualPolyline() {
      const bd = this.sprintPredictability.burndown
      if (!bd || !bd.actual || bd.actual.length === 0) {
        return '40,60 110,70 180,85 250,95 320,105 390,120 460,135 530,150'
      }
      const n = bd.actual.length
      const maxVal = Math.max(...(bd.ideal || [100]))
      const minX = 40, maxX = 580, minY = 20, maxY = 160
      const xStep = (maxX - minX) / Math.max(n - 1, 1)
      const pts = bd.actual
        .map((v, i) => v !== null && v !== undefined
          ? `${Math.round(minX + i * xStep)},${Math.round(maxY - (v / maxVal) * (maxY - minY))}`
          : null)
        .filter(Boolean)
      return pts.length > 0 ? pts.join(' ') : '40,60 110,70 180,85 250,95 320,105 390,120 460,135 530,150'
    },
  },

  watch: {
    favoriteMetricIds() {
      this.$nextTick(() => {
        this.initFavoriteCharts()
      })
    },
  },

  methods: {
    onTeamFilterChange() {
      this.selectedUserFilter = 'all'
      this.loadAllV2Data()
    },
    onUserFilterChange() {},
    applyQuickFilter() {
      this.loadAllV2Data()
    },

    // ==========================================
    // HELPERS DE APRESENTAÇÃO
    // ==========================================
    doraLevelColor(level) {
      const m = { ELITE: '#22c55e', ALTO: '#60a5fa', MEDIO: '#f59e0b', BAIXO: '#ef4444' }
      return m[(level || '').toUpperCase()] || '#60a5fa'
    },
    doraLevelLabel(level) {
      const m = { ELITE: 'Elite', ALTO: 'Alto', MEDIO: 'Médio', BAIXO: 'Baixo' }
      return m[(level || '').toUpperCase()] || level
    },
    trendColor(trend, lowerIsBetter = false) {
      if (!trend) return '#22d3ee'
      const isPositive = String(trend).startsWith('+')
      return lowerIsBetter
        ? (isPositive ? '#ef4444' : '#22c55e')
        : (isPositive ? '#22c55e' : '#ef4444')
    },
    scorecardStatusColor(status) {
      const m = { EXCELENTE: '#22c55e', BOM: '#22d3ee', MEDIO: '#f59e0b', CRITICO: '#ef4444' }
      return m[(status || '').toUpperCase()] || '#22d3ee'
    },
    deadlineRiskLabel(risk) {
      const m = { LOW: 'Baixo', MEDIUM: 'Médio', HIGH: 'Alto', CRITICAL: 'Crítico' }
      return m[(risk || '').toUpperCase()] || risk
    },
    deadlineRiskColor(risk) {
      const m = { LOW: '#22c55e', MEDIUM: '#f59e0b', HIGH: '#ef4444', CRITICAL: '#ef4444' }
      return m[(risk || '').toUpperCase()] || '#f59e0b'
    },
    qualityGateLabel(status) {
      return (status === 'PASSED' || status === 'APPROVED') ? 'Aprovado' : 'Reprovado'
    },
    qualityGateColor(status) {
      return (status === 'PASSED' || status === 'APPROVED') ? '#22c55e' : '#ef4444'
    },
    coverageBarStyle(value) {
      const pct = Math.min(Math.max(value || 0, 0), 100)
      return `flex: 1; height: 8px; background: linear-gradient(to right, #22d3ee 0%, #22d3ee ${pct}%, rgba(255,255,255,0.1) ${pct}%); border-radius: 4px;`
    },

    // ==========================================
    // CARGA DE DADOS V2
    // ==========================================
    async loadAllV2Data() {
      const params = this.getFilterParams()
      await Promise.all([
        this.loadDoraMetrics(params),
        this.loadTeamScorecard(params),
        this.loadFlowBottlenecks(params),
        this.loadQualityMetrics(params),
        this.loadInvestmentAllocation(params),
        this.loadCopilotMetrics(params),
        this.loadSprintPredictability(params),
        this.loadPeopleOps(params),
        this.loadOnCall(params),
      ])
    },

    async loadDoraMetrics(params = {}) {
      this.loadingDora = true
      try {
        const { data } = await ManagerService.getDoraMetrics(params)
        if (data.deployFrequency) this.doraMetrics.deployFrequency = { ...this.doraMetrics.deployFrequency, ...data.deployFrequency }
        if (data.leadTime)        this.doraMetrics.leadTime        = { ...this.doraMetrics.leadTime,        ...data.leadTime        }
        if (data.changeFailure)   this.doraMetrics.changeFailure   = { ...this.doraMetrics.changeFailure,   ...data.changeFailure   }
        if (data.mttr)            this.doraMetrics.mttr            = { ...this.doraMetrics.mttr,            ...data.mttr            }
        if (data.reworkRate)      this.doraMetrics.reworkRate      = { ...this.doraMetrics.reworkRate,      ...data.reworkRate      }
      } catch (e) { console.warn('DORA fallback', e) } finally { this.loadingDora = false }
    },

    async loadTeamScorecard(params = {}) {
      this.loadingScorecard = true
      try {
        const { data } = await ManagerService.getTeamScorecard(params)
        this.teamScorecard = data.teams || data || []
      } catch (e) { console.warn('Scorecard fallback', e) } finally { this.loadingScorecard = false }
    },

    async loadFlowBottlenecks(params = {}) {
      this.loadingFlow = true
      try {
        const { data } = await ManagerService.getFlowBottlenecks(params)
        if (data.prStageBreakdown) this.flowBottlenecks.prStageBreakdown = data.prStageBreakdown
        if (data.stuckPRs)         this.flowBottlenecks.stuckPRs         = data.stuckPRs
        if (data.agedWIP)          this.flowBottlenecks.agedWIP          = data.agedWIP
      } catch (e) { console.warn('Flow fallback', e) } finally { this.loadingFlow = false }
    },

    async loadQualityMetrics(params = {}) {
      this.loadingQuality = true
      try {
        const { data } = await ManagerService.getQualityMetrics(params)
        this.qualityMetrics = { ...this.qualityMetrics, ...data }
      } catch (e) { console.warn('Quality fallback', e) } finally { this.loadingQuality = false }
    },

    async loadInvestmentAllocation(params = {}) {
      this.loadingAllocation = true
      try {
        const { data } = await ManagerService.getInvestmentAllocation(params)
        this.investmentAllocation = { ...this.investmentAllocation, ...data }
      } catch (e) { console.warn('Allocation fallback', e) } finally { this.loadingAllocation = false }
    },

    async loadCopilotMetrics(params = {}) {
      this.loadingCopilot = true
      try {
        const { data } = await ManagerService.getCopilotMetrics(params)
        this.copilotMetrics = { ...this.copilotMetrics, ...data }
      } catch (e) { console.warn('Copilot fallback', e) } finally { this.loadingCopilot = false }
    },

    async loadSprintPredictability(params = {}) {
      this.loadingPredictability = true
      try {
        const { data } = await ManagerService.getSprintPredictability(params)
        this.sprintPredictability = { ...this.sprintPredictability, ...data }
      } catch (e) { console.warn('Sprint fallback', e) } finally { this.loadingPredictability = false }
    },

    async loadPeopleOps(params = {}) {
      this.loadingPeopleOps = true
      try {
        const results = await Promise.allSettled([
          ManagerService.getPendingPDIs(params),
          ManagerService.getUpcomingOneOnOnes(params),
          ManagerService.getFeedbacksStats(params),
          ManagerService.getCompetenciesStats(params),
        ])
        const [pdis, oneOnOnes, feedbacks, comp] = results
        if (pdis.status === 'fulfilled')      this.peopleOps.pendingPDIs       = pdis.value.data?.count ?? 0
        if (oneOnOnes.status === 'fulfilled') this.peopleOps.upcomingOneOnOnes = oneOnOnes.value.data?.count ?? 0
        if (feedbacks.status === 'fulfilled') this.peopleOps.feedbacks         = feedbacks.value.data?.count ?? 0
        if (comp.status === 'fulfilled')      this.peopleOps.competencias      = comp.value.data?.count ?? 0
      } catch (e) { console.warn('PeopleOps fallback', e) } finally { this.loadingPeopleOps = false }
    },

    async loadOnCall(params = {}) {
      this.loadingOnCall = true
      try {
        const { data } = await ManagerService.getOnCallStatus(params)
        this.onCall = data.oncall || data || []
      } catch (e) { console.warn('OnCall fallback', e) } finally { this.loadingOnCall = false }
    },
    getAvatarColor(name) {
      const colors = ['#22d3ee', '#2196F3', '#FF9800', '#9C27B0', '#00BCD4', '#E91E63', '#4CAF50']
      const index = name.charCodeAt(0) % colors.length
      return colors[index]
    },
    getInitials(name) {
      if (!name) return '?'
      const parts = name.split(' ')
      if (parts.length >= 2) return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase()
      return name.substring(0, 2).toUpperCase()
    },
    getInsightColor(type) {
      const colorMap = {
        'PERFORMANCE_INSIGHT': '#f87171',
        'CHURN_RISK': '#f87171',
        'ENGAGEMENT_PATTERN': '#f59e0b',
        'QUALITY_IMPROVEMENT': '#22d3ee',
      }
      return colorMap[type] || '#22d3ee'
    },
    getInsightIcon(type) {
      const iconMap = {
        'PERFORMANCE_INSIGHT': 'mdi-alert-circle',
        'CHURN_RISK': 'mdi-alert-circle',
        'ENGAGEMENT_PATTERN': 'mdi-alert',
        'QUALITY_IMPROVEMENT': 'mdi-information',
      }
      return iconMap[type] || 'mdi-lightbulb'
    },
    getSourceLabel(type) {
      const sourceMap = {
        'PERFORMANCE_INSIGHT': 'Performance',
        'CHURN_RISK': 'Risco',
        'ENGAGEMENT_PATTERN': 'Engajamento',
        'QUALITY_IMPROVEMENT': 'Qualidade',
      }
      return sourceMap[type] || type
    },
    getActionLabel(type) {
      return 'Ver mais'
    },
    async fetchInsights() {
      this.loadingInsights = true
      try {
        const { data } = await api.get('/api/ai/insights?limit=5')
        this.insights = data.insights || data || []
      } catch (error) {
        console.error('Erro ao carregar insights:', error)
        this.insights = []
      } finally {
        this.loadingInsights = false
      }
    },

    // ==========================================
    // LÓGICA DO CATÁLOGO DE MÉTRICAS E CHART.JS
    // ==========================================
    getFilterParams() {
      const params = {}
      if (this.quickDateFilter) {
        const today = new Date()
        const start = new Date()
        start.setDate(today.getDate() - this.quickDateFilter)
        params.startDate = start.toISOString().split('T')[0]
        params.endDate = today.toISOString().split('T')[0]
      }
      if (this.selectedTeamFilter && this.selectedTeamFilter !== 'all') params.teamId = this.selectedTeamFilter
      if (this.selectedUserFilter && this.selectedUserFilter !== 'all') params.userId = this.selectedUserFilter
      return params
    },

    isMetricFavorite(metricId) {
      return this.favoriteMetricIds.includes(metricId)
    },

    toggleFavoriteMetric(metricId) {
      const index = this.favoriteMetricIds.indexOf(metricId)
      if (index === -1) {
        this.favoriteMetricIds = [...this.favoriteMetricIds, metricId]
        this.loadFavoriteMetricData(metricId).then(() => this.initFavoriteCharts())
      } else {
        this.favoriteMetricIds = this.favoriteMetricIds.filter(id => id !== metricId)
        if (this.favoriteChartInstances[metricId]) {
          this.favoriteChartInstances[metricId].destroy()
          this.$delete(this.favoriteChartInstances, metricId)
        }
        this.$delete(this.favoriteMetricsData, metricId)
      }
      this.saveFavoriteMetrics()
    },

    async loadFavoriteMetrics() {
      this.loadingFavorites = true
      try {
        const response = await ManagerService.getFavoriteMetrics()
        const ids = response.data?.metricIds || []
        this.favoriteMetricIds = ids.filter(id =>
          this.allAvailableMetrics.some(m => m.id === id)
        )
        if (this.favoriteMetricIds.length > 0) {
          await this.loadAllFavoriteMetricData()
        }
      } catch (error) {
        console.error('Erro ao carregar métricas favoritas:', error)
      } finally {
        this.loadingFavorites = false
      }
    },

    async saveFavoriteMetrics() {
      try {
        await ManagerService.saveFavoriteMetrics(this.favoriteMetricIds)
      } catch (error) {
        console.error('Erro ao salvar métricas favoritas:', error)
      }
    },

    async loadAllFavoriteMetricData() {
      const params = this.getFilterParams()
      await Promise.all(
        this.favoriteMetricIds.map(id => this.loadFavoriteMetricData(id, params))
      )
      this.$nextTick(() => this.initFavoriteCharts())
    },

    async loadFavoriteMetricData(metricId, params = {}) {
      try {
        const response = await ManagerService.getMetricData(metricId, params)
        this.$set(this.favoriteMetricsData, metricId, response.data)
      } catch (error) {
        console.warn(`Sem dados do back para métrica ${metricId}, usando mock`)
      }
    },

    initFavoriteCharts() {
      this.$nextTick(() => {
        this.favoriteMetrics.forEach(metric => {
          if (metric.chartType === 'velocity-table') return

          const refName = 'favoriteChart_' + metric.id
          const refEl = this.$refs[refName]
          const canvas = Array.isArray(refEl) ? refEl[0] : refEl
          if (!canvas) return

          const apiData = this.favoriteMetricsData[metric.id]
          const config = apiData
            ? this.buildChartConfig(metric, apiData)
            : this.getMockChartConfig(metric)
          if (!config) return

          if (this.favoriteChartInstances[metric.id]) {
            this.favoriteChartInstances[metric.id].destroy()
          }

          const ctx = canvas.getContext('2d')
          this.$set(this.favoriteChartInstances, metric.id, new Chart(ctx, config))
        })
      })
    },

    getMetricLegend(metric) {
      if (metric.dynamicLegend) {
        const apiData = this.favoriteMetricsData[metric.id]
        if (apiData && apiData.datasets) {
          return apiData.datasets.map((ds, i) => ({
            label: ds.label,
            color: this.dynamicColorPalette[i % this.dynamicColorPalette.length],
          }))
        }
        return []
      }
      return metric.legend || []
    },

    buildChartConfig(metric, apiData) {
      if (!apiData || !apiData.labels || !apiData.datasets) return null

      const unitMap = {
        'github-pr-cycle-time': 'h', 'github-pr-time-breakdown': 'h',
        'github-taxa-aprovacao': '%', 'github-hotfix-rate': '%',
        'github-branch-lifetime': 'd', 'jira-task-cycle-time': 'd',
        'jira-lead-time': 'd', 'jira-burndown': '%', 'jira-bugs-rate': '%',
        'azure-build-success': '%', 'azure-pipeline-duration': 'min',
        'sonar-code-coverage': '%', 'sonar-technical-debt': 'h',
      }
      const stackedIds = ['github-pr-time-breakdown']
      const areaIds = ['github-taxa-aprovacao', 'github-hotfix-rate', 'jira-task-cycle-time', 'jira-lead-time', 'jira-burndown', 'sonar-code-coverage', 'sonar-technical-debt']
      const percentMinMaxIds = ['github-taxa-aprovacao', 'azure-build-success', 'sonar-code-coverage']

      const unit = unitMap[metric.id] || ''
      const isStacked = stackedIds.includes(metric.id)
      const isArea = areaIds.includes(metric.id)
      const isLine = metric.chartType === 'line' || metric.chartType === 'area' || isArea

      const gridColor = this.$vuetify.theme.dark ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.05)'
      const labelColor = this.$vuetify.theme.dark ? 'rgba(255,255,255,0.5)' : 'rgba(0,0,0,0.5)'

      const colorMap = {}
      this.getMetricLegend(metric).forEach(l => { colorMap[l.label] = l })

      const datasets = apiData.datasets.map(ds => {
        const entry = colorMap[ds.label] || {}
        const color = entry.color || '#22d3ee'
        const dashed = entry.dashed || false
        if (isLine) {
          return { label: ds.label, data: ds.data, borderColor: color, backgroundColor: isArea ? color + '15' : 'transparent', borderWidth: 2, fill: isArea, pointRadius: dashed ? 0 : 4, pointBackgroundColor: color, ...(dashed ? { borderDash: [5, 5] } : {}) }
        }
        return { label: ds.label, data: ds.data, backgroundColor: color, borderRadius: 4 }
      })

      return {
        type: isLine ? 'line' : metric.chartType,
        data: { labels: apiData.labels, datasets },
        options: {
          responsive: true,
          maintainAspectRatio: false,
          legend: { display: false },
          scales: {
            xAxes: [{ gridLines: { display: false }, ticks: { fontSize: 10, fontColor: labelColor }, ...(isStacked ? { stacked: true } : {}) }],
            yAxes: [{ gridLines: { color: gridColor, drawBorder: false }, ticks: { fontSize: 10, fontColor: labelColor, ...(unit ? { callback: v => v + unit } : {}), ...(percentMinMaxIds.includes(metric.id) ? { min: 0, max: 100 } : {}) }, ...(isStacked ? { stacked: true } : {}) }],
          },
          tooltips: { mode: 'index', intersect: false },
        },
      }
    },

    getMockChartConfig(metric) {
      const weeks = ['Sem 1', 'Sem 2', 'Sem 3', 'Sem 4', 'Sem 5', 'Sem 6']
      const gridColor = this.$vuetify.theme.dark ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.05)'
      const labelColor = this.$vuetify.theme.dark ? 'rgba(255,255,255,0.5)' : 'rgba(0,0,0,0.5)'
      const commonOptions = {
        responsive: true, maintainAspectRatio: false, legend: { display: false },
        scales: {
          xAxes: [{ gridLines: { display: false }, ticks: { fontSize: 10, fontColor: labelColor } }],
          yAxes: [{ gridLines: { color: gridColor, drawBorder: false }, ticks: { fontSize: 10, fontColor: labelColor } }],
        },
        tooltips: { mode: 'index', intersect: false },
      }
      const configs = {
        'github-pr-cycle-time': { type: 'line', data: { labels: weeks, datasets: [{ label: 'Frontend', data: [4, 4.2, 3.9, 3.5, 3.3, 3.2], borderColor: '#FF5722', backgroundColor: 'transparent', borderWidth: 2, pointRadius: 4, pointBackgroundColor: '#FF5722' }, { label: 'Backend', data: [6, 5.5, 5, 4.8, 4.5, 4.2], borderColor: '#2196F3', backgroundColor: 'transparent', borderWidth: 2, pointRadius: 4, pointBackgroundColor: '#2196F3' }, { label: 'Mobile', data: [4.1, 4.3, 3.8, 3.3, 3.0, 2.9], borderColor: '#FF9800', backgroundColor: 'transparent', borderWidth: 2, pointRadius: 4, pointBackgroundColor: '#FF9800' }, { label: 'Média Geral', data: [4.5, 4.2, 4.0, 3.8, 3.5, 3.4], borderColor: '#4CAF50', backgroundColor: 'transparent', borderWidth: 2, borderDash: [5, 5], pointRadius: 4, pointBackgroundColor: '#4CAF50' }] }, options: { ...commonOptions, scales: { ...commonOptions.scales, yAxes: [{ ...commonOptions.scales.yAxes[0], ticks: { callback: v => v + 'h' } }] } } },
        'github-prs-semana': { type: 'bar', data: { labels: weeks, datasets: [{ label: 'PRs Criados', data: [18, 22, 25, 20, 29, 26], backgroundColor: '#FF5722', borderRadius: 4 }, { label: 'PRs Mergeados', data: [15, 20, 23, 22, 25, 27], backgroundColor: '#4CAF50', borderRadius: 4 }] }, options: commonOptions },
        'github-pr-time-breakdown': { type: 'bar', data: { labels: weeks, datasets: [{ label: 'Abertura → Review', data: [2.5, 2.2, 2.0, 1.8, 1.7, 1.5], backgroundColor: '#FF9800' }, { label: 'Review → Merge', data: [1.5, 1.8, 1.6, 1.4, 1.3, 1.2], backgroundColor: '#2196F3' }] }, options: { ...commonOptions, scales: { xAxes: [{ ...commonOptions.scales.xAxes[0], stacked: true }], yAxes: [{ ...commonOptions.scales.yAxes[0], stacked: true, ticks: { callback: v => v + 'h' } }] } } },
        'github-top-reviewers': { type: 'bar', data: { labels: ['Ana S.', 'Carlos L.', 'Maria S.', 'Pedro C.', 'Lucas R.'], datasets: [{ label: 'Reviews', data: [42, 35, 28, 21, 18], backgroundColor: '#9C27B0', borderRadius: 4 }] }, options: commonOptions },
        'github-distribuicao-comentarios': { type: 'bar', data: { labels: weeks, datasets: [{ label: 'Comentários', data: [45, 52, 38, 61, 48, 55], backgroundColor: '#00BCD4', borderRadius: 4 }] }, options: commonOptions },
        'github-taxa-aprovacao': { type: 'line', data: { labels: weeks, datasets: [{ label: 'Aprovação (%)', data: [72, 75, 78, 80, 82, 85], borderColor: '#4CAF50', backgroundColor: 'rgba(76,175,80,0.1)', borderWidth: 2, fill: true, pointRadius: 4, pointBackgroundColor: '#4CAF50' }] }, options: { ...commonOptions, scales: { ...commonOptions.scales, yAxes: [{ ...commonOptions.scales.yAxes[0], ticks: { min: 0, max: 100, callback: v => v + '%' } }] } } },
        'github-commits-autor': { type: 'bar', data: { labels: ['Ana S.', 'Carlos L.', 'Maria S.', 'Pedro C.', 'Lucas R.'], datasets: [{ label: 'Commits', data: [68, 54, 47, 39, 32], backgroundColor: '#22d3ee', borderRadius: 4 }] }, options: commonOptions },
        'github-issues-evolucao': { type: 'bar', data: { labels: weeks, datasets: [{ label: 'Abertas', data: [12, 15, 10, 18, 14, 11], backgroundColor: '#F44336', borderRadius: 4 }, { label: 'Fechadas', data: [8, 12, 14, 10, 16, 13], backgroundColor: '#4CAF50', borderRadius: 4 }] }, options: commonOptions },
        'github-hotfix-rate': { type: 'line', data: { labels: weeks, datasets: [{ label: 'Hotfixes (%)', data: [18, 15, 12, 14, 10, 8], borderColor: '#F44336', backgroundColor: 'rgba(244,67,54,0.1)', borderWidth: 2, fill: true, pointRadius: 4, pointBackgroundColor: '#F44336' }] }, options: { ...commonOptions, scales: { ...commonOptions.scales, yAxes: [{ ...commonOptions.scales.yAxes[0], ticks: { callback: v => v + '%' } }] } } },
        'github-branch-lifetime': { type: 'line', data: { labels: weeks, datasets: [{ label: 'Dias médios', data: [3.5, 3.2, 2.9, 3.1, 2.7, 2.5], borderColor: '#607D8B', backgroundColor: 'transparent', borderWidth: 2, pointRadius: 4, pointBackgroundColor: '#607D8B' }] }, options: { ...commonOptions, scales: { ...commonOptions.scales, yAxes: [{ ...commonOptions.scales.yAxes[0], ticks: { callback: v => v + 'd' } }] } } },
        'jira-task-cycle-time': { type: 'line', data: { labels: weeks, datasets: [{ label: 'Projeto Alpha', data: [4.0, 3.8, 3.5, 3.2, 3.0, 2.8], borderColor: '#FF5722', backgroundColor: 'rgba(255,87,34,0.08)', borderWidth: 2, fill: true, pointRadius: 4, pointBackgroundColor: '#FF5722' }, { label: 'Projeto Beta', data: [3.5, 3.3, 3.0, 2.8, 2.5, 2.3], borderColor: '#2196F3', backgroundColor: 'rgba(33,150,243,0.08)', borderWidth: 2, fill: true, pointRadius: 4, pointBackgroundColor: '#2196F3' }] }, options: { ...commonOptions, scales: { ...commonOptions.scales, yAxes: [{ ...commonOptions.scales.yAxes[0], ticks: { callback: v => v + 'd' } }] } } },
        'jira-lead-time': { type: 'line', data: { labels: weeks, datasets: [{ label: 'Lead Time (dias)', data: [8, 7.5, 7, 6.5, 6, 5.5], borderColor: '#9C27B0', backgroundColor: 'rgba(156,39,176,0.08)', borderWidth: 2, fill: true, pointRadius: 4, pointBackgroundColor: '#9C27B0' }] }, options: { ...commonOptions, scales: { ...commonOptions.scales, yAxes: [{ ...commonOptions.scales.yAxes[0], ticks: { callback: v => v + 'd' } }] } } },
        'jira-burndown': { type: 'line', data: { labels: ['Dia 1', 'Dia 3', 'Dia 5', 'Dia 7', 'Dia 9', 'Dia 11'], datasets: [{ label: 'Ideal', data: [100, 83, 66, 50, 33, 0], borderColor: '#2196F3', backgroundColor: 'transparent', borderWidth: 2, borderDash: [5, 5], pointRadius: 0 }, { label: 'Real', data: [100, 88, 72, 58, 40, 12], borderColor: '#22d3ee', backgroundColor: 'rgba(34,211,238,0.08)', borderWidth: 2, fill: true, pointRadius: 4, pointBackgroundColor: '#22d3ee' }] }, options: { ...commonOptions, scales: { ...commonOptions.scales, yAxes: [{ ...commonOptions.scales.yAxes[0], ticks: { callback: v => v + '%' } }] } } },
        'jira-bugs-rate': { type: 'bar', data: { labels: ['Projeto Alpha', 'Projeto Beta', 'Projeto Gamma'], datasets: [{ label: 'Bugs (%)', data: [12, 8, 5], backgroundColor: '#F44336', borderRadius: 4 }] }, options: { ...commonOptions, scales: { ...commonOptions.scales, yAxes: [{ ...commonOptions.scales.yAxes[0], ticks: { callback: v => v + '%' } }] } } },
        'jira-throughput': { type: 'bar', data: { labels: weeks, datasets: [{ label: 'Tasks concluídas', data: [18, 22, 19, 25, 21, 24], backgroundColor: '#4CAF50', borderRadius: 4 }] }, options: commonOptions },
        'azure-build-success': { type: 'line', data: { labels: weeks, datasets: [{ label: 'Sucesso (%)', data: [88, 91, 89, 93, 95, 94], borderColor: '#4CAF50', backgroundColor: 'rgba(76,175,80,0.1)', borderWidth: 2, fill: true, pointRadius: 4, pointBackgroundColor: '#4CAF50' }] }, options: { ...commonOptions, scales: { ...commonOptions.scales, yAxes: [{ ...commonOptions.scales.yAxes[0], ticks: { min: 0, max: 100, callback: v => v + '%' } }] } } },
        'azure-deploy-frequency': { type: 'bar', data: { labels: weeks, datasets: [{ label: 'Deploys', data: [4, 6, 5, 8, 7, 9], backgroundColor: '#2196F3', borderRadius: 4 }] }, options: commonOptions },
        'azure-pipeline-duration': { type: 'line', data: { labels: weeks, datasets: [{ label: 'Duração (min)', data: [12, 11, 13, 10, 9, 8], borderColor: '#FF9800', backgroundColor: 'transparent', borderWidth: 2, pointRadius: 4, pointBackgroundColor: '#FF9800' }] }, options: { ...commonOptions, scales: { ...commonOptions.scales, yAxes: [{ ...commonOptions.scales.yAxes[0], ticks: { callback: v => v + 'min' } }] } } },
        'sonar-code-coverage': { type: 'line', data: { labels: weeks, datasets: [{ label: 'Cobertura (%)', data: [62, 65, 68, 70, 72, 75], borderColor: '#4CAF50', backgroundColor: 'rgba(76,175,80,0.1)', borderWidth: 2, fill: true, pointRadius: 4, pointBackgroundColor: '#4CAF50' }] }, options: { ...commonOptions, scales: { ...commonOptions.scales, yAxes: [{ ...commonOptions.scales.yAxes[0], ticks: { min: 0, max: 100, callback: v => v + '%' } }] } } },
        'sonar-code-smells': { type: 'bar', data: { labels: weeks, datasets: [{ label: 'Code Smells', data: [120, 105, 98, 88, 80, 72], backgroundColor: '#FF9800', borderRadius: 4 }] }, options: commonOptions },
        'sonar-technical-debt': { type: 'line', data: { labels: weeks, datasets: [{ label: 'Horas estimadas', data: [48, 42, 38, 34, 30, 26], borderColor: '#9C27B0', backgroundColor: 'rgba(156,39,176,0.08)', borderWidth: 2, fill: true, pointRadius: 4, pointBackgroundColor: '#9C27B0' }] }, options: { ...commonOptions, scales: { ...commonOptions.scales, yAxes: [{ ...commonOptions.scales.yAxes[0], ticks: { callback: v => v + 'h' } }] } } },
      }
      return configs[metric.id] || null
    },

    getSourceColor(source) {
      const colors = { 'GitHub': '#6e5494', 'Jira': '#2684FF', 'Azure Pipelines': '#0078D7', 'SonarQube': '#CB202D' }
      return colors[source] || '#22d3ee'
    },

    getSourceIcon(source) {
      const icons = { 'GitHub': 'mdi-github', 'Jira': 'mdi-jira', 'Azure Pipelines': 'mdi-microsoft-azure-devops', 'SonarQube': 'mdi-bug-check-outline' }
      return icons[source] || 'mdi-chart-line'
    },

    getCategoryColor(category) {
      const colors = { 'Code Review': '#9C27B0', 'Entrega': '#2196F3', 'Qualidade': '#4CAF50', 'CI/CD': '#FF9800', 'Produtividade': '#607D8B' }
      return colors[category] || 'grey'
    },
  },

  mounted() {
    this.fetchInsights()
    this.loadFavoriteMetrics()
    this.loadAllV2Data()
  },

  beforeDestroy() {
    Object.values(this.favoriteChartInstances).forEach(chart => {
      if (chart) chart.destroy()
    })
  }
}
</script>

<style scoped>
.dm-wrapper {
  background-color: var(--bg-main, #0D0D0D) !important;
  max-width: 1440px;
  margin: 0 auto;
}

.theme--dark {
  --dm-bg-base: #0A0A0B;
  --dm-card-bg: #111113;
  --dm-border-color: rgba(255, 255, 255, 0.07);
  --dm-text-main: #FFFFFF;
  --dm-text-muted: rgba(255, 255, 255, 0.55);
}

.theme--light {
  --dm-bg-base: #F5F7FA;
  --dm-card-bg: #FFFFFF;
  --dm-border-color: rgba(0, 0, 0, 0.08);
  --dm-text-main: #1D1D1F;
  --dm-text-muted: rgba(0, 0, 0, 0.55);
}

.dm-text-main { color: var(--dm-text-main) !important; }
.dm-text-muted { color: var(--dm-text-muted) !important; }
.border-top { border-top: 1px solid var(--dm-border-color); }
.border-bottom { border-bottom: 1px solid var(--dm-border-color); }

/* Header */
.dm-header-icon {
  width: 56px; height: 56px; border-radius: 16px;
  display: flex; align-items: center; justify-content: center;
  background: rgba(34, 211, 238, 0.1); border: 1px solid rgba(34, 211, 238, 0.2);
}
.dm-header-title {
  font-size: 26px;
  font-weight: 800;
  letter-spacing: -0.02em;
  color: var(--dm-text-main);
  display: flex;
  align-items: center;
  gap: 12px;
}
.dm-header-version {
  font-size: 12px;
  font-weight: 600;
  background-color: rgba(239, 68, 68, 0.15);
  color: #ef4444;
  padding: 2px 6px;
  border-radius: 4px;
  white-space: nowrap;
}
.dm-header-subtitle { font-size: 14px; color: var(--dm-text-muted); margin-top: 2px; }

/* Inputs */
::v-deep .dm-input .v-input__slot {
  background-color: var(--dm-card-bg) !important;
  border-color: var(--dm-border-color) !important;
}
::v-deep .dm-input input, ::v-deep .dm-input .v-select__selection {
  color: var(--dm-text-main) !important;
}
::v-deep .dm-input .v-text-field__details { display: none !important; }

/* Cards V2 */
.dm-v2-card {
  background-color: var(--dm-card-bg) !important;
  border: 1px solid var(--dm-border-color) !important;
  border-radius: 16px !important;
  transition: all 0.2s ease;
}

.dm-hover-card:hover {
  transform: translateY(-4px);
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.2) !important;
  border-color: rgba(255, 255, 255, 0.15) !important;
}

.ai-glow-card {
  border: 1px solid rgba(34, 211, 238, 0.2) !important;
  background: linear-gradient(180deg, var(--dm-card-bg) 0%, rgba(34, 211, 238, 0.03) 100%) !important;
}

/* Scorecard Table */
.dm-scorecard-table {
  width: 100%;
  overflow-x: auto;
}

.dm-scorecard-cell {
  padding: 12px 16px;
  text-align: left;
  font-size: 14px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.05);
  font-weight: 500;
}

.dm-scorecard-header {
  background-color: rgba(255, 255, 255, 0.02);
  color: var(--dm-text-muted);
  font-size: 12px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
}

/* DORA Cards */
.dm-dora-row {
  display: flex;
  gap: 16px;
  overflow-x: auto;
  padding-bottom: 8px;
  flex-wrap: nowrap;
}

.dm-dora-card {
  flex: 1;
  min-width: 200px;
  border-radius: 12px !important;
  display: flex;
  flex-direction: column;
}

.dm-dora-chart {
  height: 3px;
  border-radius: 2px;
  margin-top: auto;
}

/* Áreas de Mock para Gráficos */
.dm-mock-area {
  background: repeating-linear-gradient(
    45deg,
    transparent,
    transparent 10px,
    var(--dm-border-color) 10px,
    var(--dm-border-color) 11px
  );
  border-radius: 8px;
  min-height: 180px;
  opacity: 0.5;
}

/* Ajustes Responsivos do Scorecard */
@media (max-width: 960px) {
  .dm-scorecard-row .flex-md-grow-1 {
    min-width: 50% !important;
    max-width: 50% !important;
  }
}
@media (max-width: 600px) {
  .dm-scorecard-row .flex-md-grow-1 {
    min-width: 100% !important;
    max-width: 100% !important;
  }
}
</style>